
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MailOpen, MousePointerClick, Mail, AlertCircle } from "lucide-react";

interface DeliveryStats {
  total: number;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  failed: number;
}

interface EmailEngagementMetricsProps {
  deliveryStats?: DeliveryStats;
  isLoading: boolean;
}

export function EmailEngagementMetrics({ deliveryStats, isLoading }: EmailEngagementMetricsProps) {
  if (isLoading) {
    return (
      <>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-8 w-1/3 mb-2" />
            <Skeleton className="h-14 w-1/2 mb-4" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-8 w-1/3 mb-2" />
            <Skeleton className="h-14 w-1/2 mb-4" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-8 w-1/3 mb-2" />
            <Skeleton className="h-14 w-1/2 mb-4" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-8 w-1/3 mb-2" />
            <Skeleton className="h-14 w-1/2 mb-4" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
      </>
    );
  }

  // Fallback for stats
  const stats = deliveryStats || {
    total: 0,
    sent: 0,
    delivered: 0,
    opened: 0,
    clicked: 0,
    bounced: 0,
    complained: 0,
    failed: 0
  };

  // Calculate rates
  const deliveredRate = stats.total > 0 ? (stats.delivered / stats.total) * 100 : 0;
  const openRate = stats.delivered > 0 ? (stats.opened / stats.delivered) * 100 : 0;
  const clickRate = stats.opened > 0 ? (stats.clicked / stats.opened) * 100 : 0;
  const bounceRate = stats.total > 0 ? (stats.bounced / stats.total) * 100 : 0;

  return (
    <>
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2 text-slate-700">
            <Mail className="h-4 w-4" />
            <span className="text-sm font-medium">Delivered</span>
          </div>
          <div className="text-2xl font-semibold text-slate-900 mb-1">
            {stats.delivered.toLocaleString()}
          </div>
          <div className="text-xs text-slate-600">
            <span className={`${deliveredRate >= 95 ? "text-emerald-600" : "text-amber-600"}`}>
              {deliveredRate.toFixed(1)}% delivery rate
            </span>
            {" "} of {stats.total.toLocaleString()} sent
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2 text-slate-700">
            <MailOpen className="h-4 w-4" />
            <span className="text-sm font-medium">Opens</span>
          </div>
          <div className="text-2xl font-semibold text-slate-900 mb-1">
            {stats.opened.toLocaleString()}
          </div>
          <div className="text-xs text-slate-600">
            <span className={`${openRate >= 20 ? "text-emerald-600" : openRate >= 10 ? "text-amber-600" : "text-slate-600"}`}>
              {openRate.toFixed(1)}% open rate
            </span>
            {" "} from delivered emails
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2 text-slate-700">
            <MousePointerClick className="h-4 w-4" />
            <span className="text-sm font-medium">Clicks</span>
          </div>
          <div className="text-2xl font-semibold text-slate-900 mb-1">
            {stats.clicked.toLocaleString()}
          </div>
          <div className="text-xs text-slate-600">
            <span className={`${clickRate >= 10 ? "text-emerald-600" : clickRate >= 5 ? "text-amber-600" : "text-slate-600"}`}>
              {clickRate.toFixed(1)}% click-through rate
            </span>
            {" "} from opened emails
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2 text-slate-700">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm font-medium">Bounces</span>
          </div>
          <div className="text-2xl font-semibold text-slate-900 mb-1">
            {stats.bounced.toLocaleString()}
          </div>
          <div className="text-xs text-slate-600">
            <span className={`${bounceRate <= 2 ? "text-emerald-600" : bounceRate <= 5 ? "text-amber-600" : "text-red-600"}`}>
              {bounceRate.toFixed(1)}% bounce rate
            </span>
            {" "} from total sent
          </div>
        </CardContent>
      </Card>
    </>
  );
}
